import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface WizardFooterProps {
  step: number;
  onBack: () => void;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  isLoading?: boolean;
}

export function WizardFooter({
  step,
  onBack,
  onContinue,
  continueLabel = "Continue",
  continueDisabled = false,
  isLoading = false,
}: WizardFooterProps) {
  return (
    <div className="flex items-center justify-center gap-3 px-6 py-3.5 border-t border-border/50 bg-background shrink-0">
      <div>
        {step > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                onClick={onBack}
                className="gap-1.5 text-muted-foreground/70 hover:text-foreground h-9 px-3 text-lg transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-left">
              <p className="text-sm leading-relaxed">Return to the previous step. Your answers on this page are saved.</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={onContinue}
            disabled={continueDisabled || isLoading}
            className={cn(
              "gap-2 min-w-[120px] h-9 text-lg font-medium rounded-lg transition-all duration-200",
              "bg-blue-600 hover:bg-blue-500 text-white",
              !continueDisabled && !isLoading
                ? "hover:shadow-md hover:shadow-blue-600/20 active:scale-[0.98]"
                : "opacity-40 cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {continueLabel}
                {continueLabel === "Continue" && <ArrowRight className="h-3.5 w-3.5" />}
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-left">
          <p className="text-sm leading-relaxed">
            {continueLabel === "Build Ecosystem"
              ? "Save all answers and create the entity ecosystem."
              : continueLabel === "Confirm & Save"
              ? "Save this entity to your workspace."
              : continueLabel === "Waiting for scan…"
              ? "The website scan is still running. Continue once it finishes."
              : "Save your answer and continue to the next step."}
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
